/*
 *  Copyright (c) Qwoach LLC 2020. All rights reserved.
 *  Unauthorized copying of this file, via any medium is strictly prohibited
 *  Proprietary and confidential
 *  Created by Artem Bogush <artem@qwoach.com> on 7/10/2020
 */

import DbBaseObject from "@shared/DbBaseObject.js";
import {plainToClass, Type, Transform} from "class-transformer";
import {classToClass} from "@qwoach/class-transformer-wrapper";
import {TimeInterval} from "@/core/js/model/scheduler/TimeInterval";
import dayjs from "dayjs";
import {enumerateDaysBetweenDates} from "@/utils/helpers";

export const DayOfWeekEnum = Object.freeze({
	SUN: 0,
	MON: 1,
	TUE: 2,
	WED: 3,
	THU: 4,
	FRI: 5,
	SAT: 6,
})

let oneTimeIntervalsTransform = (oneTimeIntervals) => {
	oneTimeIntervals = oneTimeIntervals.value || oneTimeIntervals
	Object.keys(oneTimeIntervals).forEach(key => {
		oneTimeIntervals[key] = plainToClass(TimeInterval, oneTimeIntervals[key])
	})
	return oneTimeIntervals;
}

export default class AvailabilitySettings extends DbBaseObject{

	@Type(() => TimeInterval)
	mon: TimeInterval[] = [];
	@Type(() => TimeInterval)
	tue: TimeInterval[] = [];
	@Type(() => TimeInterval)
	wed: TimeInterval[] = [];
	@Type(() => TimeInterval)
	thu: TimeInterval[] = [];
	@Type(() => TimeInterval)
	fri: TimeInterval[] = [];
	@Type(() => TimeInterval)
	sat: TimeInterval[] = [];
	@Type(() => TimeInterval)
	sun: TimeInterval[] = [];

	@Transform(oneTimeIntervalsTransform)
	oneTimeIntervals = {} //{'dd-mm-yyyy1': TimeInterval[], 'dd-mm-yyyy2': TimeInterval[] etc...}

	_timezoneDifference = 0 //needed to calculate timeslots based on the client-coach timezone difference. This is a dynamic field and we don't store it in the db

	constructor() {
		super();
	}



	byDayIndex(index: number){
		switch (index) {
			case DayOfWeekEnum.SUN:
				return this.sun;
			case DayOfWeekEnum.MON:
				return this.mon;
			case DayOfWeekEnum.TUE:
				return this.tue;
			case DayOfWeekEnum.WED:
				return this.wed;
			case DayOfWeekEnum.THU:
				return this.thu;
			case DayOfWeekEnum.FRI:
				return this.fri;
			case DayOfWeekEnum.SAT:
				return this.sat;
		}
	}

	trimIntervals(startTs: number, endTs: number, intervals: TimeInterval[]) {
		console.log("AvailabilitySettings.trimIntervals: startTs = ", startTs, ", endTs = ", endTs, ", intervals = " + intervals, ", as array = ", intervals)

		let finalIntervals = []
		intervals.forEach(interval => {
			if(interval.startTime < startTs){
				interval._startTimeBeforeTrim = interval.startTime
				interval.startTimeRelative +=  startTs - interval.startTime
				interval.startTime = startTs;
			}
			if(interval.endTime > endTs){
				interval._endTimeBeforeTrim = interval.endTime
				interval.endTimeRelative -=  interval.endTime - endTs
				interval.endTime = endTs;
			}
			if(interval.startTime < interval.endTime){
				finalIntervals.push(interval)
			}
		})
		console.log("AvailabilitySettings.trimIntervals: result = " + finalIntervals, ", as array = ", finalIntervals)

		return finalIntervals
	}

	recurringIntervalsByDate(day: Date, eventType, moveOverDates, timezoneShift){
		// let intervals = this.threeDaysIntervals(day, eventType, (date, eventType) => this.recurringIntervalsByDateInternal(date, eventType))
		let intervals = this.recurringIntervalsByDateInternal(day, eventType, moveOverDates, timezoneShift)
		return intervals
	}

	oneTimeIntervalsByDate(day: Date, eventType, moveOverDates, timezoneShift){
		// let intervals = this.threeDaysIntervals(day, eventType, (date, eventType) => this.oneTimeIntervalsByDateInternal(date, eventType))
		let intervals = this.oneTimeIntervalsByDateInternal(day, eventType, moveOverDates, timezoneShift)
		return intervals
	}
	oneTimeIntervalsByDateInternal(day: Date, eventType, moveOverDates, timezoneShift){
		let oneTimeIndex = this.dayToOneTimeIndex(day);
		let intervals = this.oneTimeIntervals[oneTimeIndex]
		console.log("oneTimeIntervalsByDateInternal: intervals1 = " + intervals + ", oneTimeIndex = ", oneTimeIndex, ", day = ", day)
		if(intervals){
			intervals = this.applyDateToIntervals(day, intervals, eventType, moveOverDates, timezoneShift)
			// intervals = this.removePastIntervals(intervals, eventType)
		}
		if(intervals){
			console.log("oneTimeIntervalsByDateInternal: intervals2 = " + intervals + ", oneTimeIndex = ", oneTimeIndex, ", day = ", day)
		}
		return intervals
	}

	threeDaysIntervals(day: Date, eventType, intervalFunction){

		let dayStartTs = dayjs(day).startOf('day').valueOf()
		let dayEndTs = dayjs(day).endOf('day').valueOf()

		let yesterday = dayjs(day).add(-1, 'days').toDate()
		let tomorrow = dayjs(day).add(1, 'days').toDate()

		console.log("yesterday:" + yesterday)
		let intervalsYesterday = intervalFunction(yesterday, eventType);
		console.log("today:" + day)
		let intervalsNow = intervalFunction(day, eventType);
		console.log("tomorrow:" + tomorrow)
		let intervalsTomorrow = intervalFunction(tomorrow, eventType);

		console.log("today = ", day)
		// console.log("threeDaysIntervals: day = ", day, ", yesterday = ", yesterday, ", tomorrow = ", tomorrow)
		// console.log("threeDaysIntervals: intervalsNow = ", intervalsNow, ", intervalsYesterday = ", intervalsYesterday, ", intervalsTomorrow = ", intervalsTomorrow)
		// console.log("threeDaysIntervals: intervalsNow2 = " + intervalsNow, ", intervalsYesterday = " + intervalsYesterday, ", intervalsTomorrow = " + intervalsTomorrow)
		let intervalsAll = [...intervalsYesterday, ...intervalsNow, ...intervalsTomorrow]
		// let intervalsAll = [...intervalsNow]
		let intervals = this.trimIntervals(dayStartTs, dayEndTs, intervalsAll)
		return intervals
	}

	recurringIntervalsByDateInternal(day: Date, eventType, moveOverDates, timezoneShift){
		let dayIndex = this.dayToRecurringIndex(day);

		let intervals = this.byDayIndex(dayIndex);
		// console.log("rec dayIndex = ", dayIndex, ", this = ", this, ", timezoneShift = ", timezoneShift)
		if(intervals){
			intervals = this.applyDateToIntervals(day, intervals, eventType, moveOverDates, timezoneShift)
			// intervals = this.removePastIntervals(intervals, eventType)
		}
		return intervals
	}

	setRecurringTimeIntervalsByDate(day: Date, intervals: TimeInterval[]){
		this.sortIntervals(intervals)
		let index = this.dayToRecurringIndex(day);
		this.setRecurringTimeIntervalsByDayIndex(index, intervals)
	}
	setRecurringTimeIntervalsByDayIndex(dayIndex: number, intervals: TimeInterval[]){
		this.sortIntervals(intervals)
		let array = this.byDayIndex(dayIndex)
		array.splice(0, array.length)
		array.push(...intervals)
	}



	// removePastIntervals(intervals: TimeInterval[], eventType): TimeInterval[]{
	// 	let resIntervals = [];
	// 	let startTime = new Date().getTime();// + ((eventType || {}).minSchedulingNotice) || 0
	// 	console.log("removePastIntervals: intervals = ", intervals)
	// 	intervals.forEach(interval => {
	// 		console.log("removePastIntervals: interval.endTime = ", interval.endTime)
	// 		console.log("removePastIntervals: startTime = ", startTime)
	// 		if(interval.endTime > startTime){
	// 			let intervalStartTime = Math.max(startTime, interval.startTime)
	// 			resIntervals.push(new TimeInterval(intervalStartTime, interval.endTime))
	// 		}
	// 	})
	//
	// 	return resIntervals;
	// }

	applyDateToIntervals(date: Date, intervals: TimeInterval[], eventType, moveOverDates, timezoneShift): TimeInterval[]{
		let res = []
		intervals.forEach(interval => {
			let newInterval = classToClass(interval)
			newInterval.applyDate(date, moveOverDates, eventType, timezoneShift)
			res.push(newInterval)
		})
		return res;
	}

	hasAvailability(date: Date, eventType): boolean{
		let intervals = this.intervalsByDate(date, eventType);
		return (intervals || []).length > 0
	}

	intervalsByDate(date: Date, eventType): TimeInterval[]{
		if(!date) return null
		// let intervals = this.threeDaysIntervals(date, eventType, (date, eventType) => this.intervalsByDateInternal(date, eventType))
		let intervals = this.intervalsByDateInternal(date, eventType)
		return intervals
	}
	intervalsByDateInternal(date: Date, eventType, moveOverDates): TimeInterval[]{
		if(!date) return null
		let res = this.oneTimeIntervalsByDate(date, eventType, moveOverDates, this._timezoneDifference)
		if(!res){
			res = this.recurringIntervalsByDate(date, eventType, moveOverDates, this._timezoneDifference)
		}
		// console.log("intervalsByDate: date = ", date, ", day utc = ", date.getUTCDay(), ", day = ", date.getDay(), ", intervals = " + res)
		return res
	}
	isRecurringIntervalsByDate(date: Date, eventType){
		let res = this.oneTimeIntervalsByDate(date, eventType)
		return res == null
	}
	sortIntervals(intervals: TimeInterval[]){
		intervals.sort((l, r) => {
			return l.startTime - r.startTime
		})
	}

	setOneTimeIntervalsByDate(day: Date, intervals: TimeInterval[]){
		this.sortIntervals(intervals)
		let oneTimeIndex = this.dayToOneTimeIndex(day);
		this.oneTimeIntervals[oneTimeIndex] = intervals;
		// console.log("set oneTimeIntervalsByDate: day = ", day, ", oneTimeIndex = ", oneTimeIndex, ", intervals = " + intervals)

	}

	clearOneTimeIntervalsByWeekDay(day: Date){
		let index = this.dayToRecurringIndex(day);
		this.clearOneTimeIntervalsByDayIndex(index)
	}
	clearOneTimeIntervalsByDayIndex(dayIndex: number){

		Object.keys(this.oneTimeIntervals).forEach(key => {
			let date = this.oneTimeIndexToDate(key)
			let index = this.dayToRecurringIndex(date);
			if(index == dayIndex){
				// delete this.oneTimeIntervals[key];//this won't work if database uses set+merge to update data
				this.oneTimeIntervals[key] = null;
			}
		})
	}

	findOneTimeIntervalsByDayIndex(dayIndex: number){
		let intervals = []
		Object.keys(this.oneTimeIntervals).forEach(key => {
			let date = this.oneTimeIndexToDate(key)
			let index = this.dayToRecurringIndex(date);
			console.log("findOneTimeIntervalsByDayIndex: index = ", index, ", dayIndex = ", dayIndex)
			let dayIntervals = this.oneTimeIntervals[key]
			if(dayIndex == index && dayIntervals){
				intervals.push(dayIntervals);
			}
		})
		return intervals;
	}



	dayToOneTimeIndex(day: Date){
		let dateStr = dayjs(day).format("MM-DD-YYYY")
		return dateStr;
	}
	oneTimeIndexToDate(index: String){
		let date = dayjs(index, "MM-DD-YYYY").toDate();
		return date;
	}

	dayToRecurringIndex(day: Date){
		let dateStr = day.getDay()
		return dateStr;
	}
	recurringIndexToDayName(dayIndex: number){
		return this.dayOfWeekAsString(dayIndex);
	}

	dayOfWeekAsString(dayIndex) {
		return ["Sunday", "Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"][dayIndex];
	}


	subtractBusyIntervals(busyIntervals: TimeInterval[], eventType): AvailabilitySettings{
		let settings = classToClass(this)
		settings._timezoneDifference = this._timezoneDifference
		// settings = settings.transposeToCurrentTimezone()
		busyIntervals.forEach(busyTimeInterval => {
			// console.log("busyTimeInterval = " + busyTimeInterval)
			let startDate = new Date(busyTimeInterval.startTime)
			let endDate = new Date(busyTimeInterval.endTime)
			// console.log("subtractBusyIntervals: startDate = ", new Date(startDate), ", endDate = ", new Date(endDate))

			let intervals = []
			enumerateDaysBetweenDates(startDate, endDate).forEach(date => {
				// console.log("subtractBusyIntervals: date = ", date, ", busyTimeInterval = " + busyTimeInterval)
				let res = settings.subtractBusyIntervalsByDate(date, busyTimeInterval, eventType)
				if(res){
					settings.setOneTimeIntervalsByDate(date, res)
				}
			})
		})
		return settings;
	}

	subtractBusyIntervalsByDate(date: Date, busyTimeInterval: TimeInterval, eventType){
		// console.log("subtractBusyIntervalsByDate: date = ", date)
		let intervalsByDate = this.intervalsByDate(date, eventType);
		// console.log("subtractBusyIntervalsByDate: intervalsByDate = " + intervalsByDate)
		let finalIntervalsByDate = this.subtractBusyIntervalsByDateInternal(intervalsByDate, busyTimeInterval, eventType)
		// console.log("subtractBusyIntervalsByDate: finalIntervalsByDate = " + finalIntervalsByDate)
		return finalIntervalsByDate
	}

	 subtractBusyIntervalsByDateInternal(intervalsByDate: TimeInterval[], busyTimeInterval: TimeInterval, eventType): TimeInterval[]{
		// console.log("eventType = ", intervalsByDate, ", busyTimeInterval = ", busyTimeInterval)

		 let bufferBefore = Math.max(busyTimeInterval.bufferBeforeMs || 0, eventType.eventBufferBefore)
		 let bufferAfter  = Math.max(busyTimeInterval.bufferAfterMs || 0, eventType.eventBufferAfter)
		 // console.log("busyTimeInterval: " + busyTimeInterval + ", bufferBeforeMs = ", busyTimeInterval.bufferBeforeMs, ", bufferAfterMs = ", busyTimeInterval.bufferAfterMs)
		busyTimeInterval = new TimeInterval(busyTimeInterval.startTime - bufferBefore, busyTimeInterval.endTime + bufferAfter)
		let hasIntersectionGlobal = false;
		let res = []
		// console.log("subtractBusyIntervalsByDateInternal: intervalsByDate = ", intervalsByDate, ", busyTimeInterval = ", busyTimeInterval)
		intervalsByDate.forEach(timeInterval => {
			let hasIntersection = this.hasIntersectionTimeInterval(timeInterval, busyTimeInterval)
			// console.log("hasIntersection = ", hasIntersection)
			if(hasIntersection){
				let newIntervals = this.subtractIntervals(timeInterval, busyTimeInterval);
				res.push(...newIntervals)
			} else {
				res.push(timeInterval)
			}
			hasIntersectionGlobal |= hasIntersection;
		})

		// console.log("busyTimeInterval before: " + intervalsByDate)
		// console.log("busyTimeInterval after: ", res.map(interval => interval.toString()))

		return hasIntersectionGlobal ? res : null;
	}

	hasIntersectionTimeInterval(left: TimeInterval, right: TimeInterval): Boolean{
		return left.startTime < right.endTime && (left.endTime + left.bufferAfterMs) > right.startTime
	}
	isIntervalFullyContained(containerInterval: TimeInterval, right: TimeInterval): Boolean{
		return containerInterval.startTime <= right.startTime && containerInterval.endTime >= right.endTime
	}

	subtractIntervals(left: TimeInterval, right: TimeInterval): TimeInterval[]{
		let res = [];

		if (right.endTime < left.endTime){
			let interval = new TimeInterval(right.endTime, left.endTime)
			interval._endTimeBeforeTrim = left._endTimeBeforeTrim
			interval._alreadyTransposed = left._alreadyTransposed
			interval._canExtendBufferAfter = left._canExtendBufferAfter
			res.push(interval)
		}
		if (right.startTime > left.startTime){
			let interval = new TimeInterval(left.startTime, Math.min(left.endTime, right.startTime))
			interval._startTimeBeforeTrim = left._startTimeBeforeTrim
			interval._alreadyTransposed = left._alreadyTransposed
			interval._canExtendBufferAfter = false
			res.push(interval)
		}

		console.log("subtractIntervals: left = " + left, "right = " + right, ", result = " + res)

		return res;
	}

	canScheduleMoreSessionsForDay(day: Date, eventType, scheduledCountByDays = {}){

		let res = true;
		let countDay = 0
		let countWeek = 0

		if(eventType.maxEventsPerDay > 0){
			let oneTimeIndex = this.dayToOneTimeIndex(day)
			countDay = scheduledCountByDays[oneTimeIndex] || 0
		}
		if(eventType.maxEventsPerWeek > 0 ){
			let startOfWeek = dayjs(day).startOf("week")
			let endOfWeek = dayjs(day).endOf("week")
			enumerateDaysBetweenDates(startOfWeek, endOfWeek).forEach(dayInWeek => {
				let oneTimeIndex = this.dayToOneTimeIndex(dayInWeek)
				countWeek += scheduledCountByDays[oneTimeIndex] || 0
			})
		}
		let canScheduleForDay = eventType.maxEventsPerDay > 0 ? countDay < eventType.maxEventsPerDay : true
		let canScheduleForWeek = eventType.maxEventsPerWeek > 0 ? countWeek < eventType.maxEventsPerWeek : true
		res = canScheduleForDay && canScheduleForWeek

		return res
	}

	getAvailabilityHoursForDayAll(day, eventType, scheduledCountByDays){
		day = new Date(day) //if we don't apply this, getAvailabilityHoursForDay returns wierd truncated data AFTER the "timeInterval.applyDate" is called. No clue what's going on. At all.
		let res = []

		let timeOptionsAvailable = this.getAvailabilityHoursForDay(day, eventType, scheduledCountByDays)

		let timeInterval = new TimeInterval(0, 24 * 60 * 60 * 1000)
		// timeInterval.applyDate(day, false, eventType, this._timezoneDifference)
		timeInterval.startTime = day.getTime() + timeInterval.startTime
		timeInterval.endTime = day.getTime() + timeInterval.endTime

		let timeOptionsUnavailable = this.getAvailabilityHoursFromTimeInterval(timeInterval, eventType, {ignoreMinSchedulingNotice: true, ignoreMaxSchedulingNotice: true, ignoreDuration: true})


		timeOptionsAvailable.forEach(timeOption => {
			res.push({timeOption})
		})

		timeOptionsUnavailable.forEach(hourTsUnavailable => {
			let hasOption = false
			let startTime = hourTsUnavailable
			let endTime = startTime + eventType.duration
			for(let hourTsAvailable of timeOptionsAvailable){
				if(hourTsAvailable >= startTime && hourTsAvailable < endTime){
					hasOption = true
					break
				}
			}
			if(!hasOption){
				res.push({timeOption: hourTsUnavailable, unavailable: true})
			}
		})

		res = res.sort((l, r) => {
			return l.timeOption - r.timeOption
		})

		console.log("getAvailabilityHoursForDayAll: res = ", res)

		return res;

	}

	getAvailabilityHoursForDay(day, eventType, scheduledCountByDays){
		let timeIntervals = []
		if(this.canScheduleMoreSessionsForDay(day, eventType, scheduledCountByDays)){
			timeIntervals = this.intervalsByDate(day, eventType) || [];
		}

		let hours = [];

		for (let i = 0; i < timeIntervals.length; ++i){
			let timeInterval = timeIntervals[i];
			hours.push(... this.getAvailabilityHoursFromTimeInterval(timeInterval, eventType))
		}
		// console.log("getAvailabilityHoursForDay: hours = ", hours)

		return hours;
	}

	getAvailabilityHoursFromTimeInterval(timeInterval: TimeInterval, eventType, options = {}){
		let {ignoreMinSchedulingNotice, ignoreMaxSchedulingNotice, ignoreDuration} = options
		let res = [];
		let now = new Date().getTime()
		let minTime = now// + ((eventType || {}).minSchedulingNotice) || 0
		if(!ignoreMinSchedulingNotice){
			minTime += eventType.minSchedulingNotice
		}
		let maxTime = 99999999999999;
		let rollingDays = eventType.rollingDays
		if(rollingDays > 0 && !ignoreMaxSchedulingNotice){
			if(rollingDays < 100){
				//old format was days - convert it to ms
				rollingDays = rollingDays * (24 * 60 * 60 * 1000)
			}
			maxTime = now + rollingDays
		}
		let currentTime = timeInterval.startTime;

		//TODO round up to fit the "increment" grid - this produces UX problems if user sets their interval outside of the grid. This needs to be smarter.
		// currentTime = Math.ceil(currentTime / eventType.increments) * eventType.increments

		let eventEnd = eventType.duration + eventType.eventBufferAfter
		if(ignoreDuration){
			eventEnd = 1
		}
		let intervalBoundary = timeInterval.endTime

		let isBoundaryExtended = false
		if(timeInterval._endTimeBeforeTrim){
			// console.log("timeInterval.endTime1 = ", timeInterval.endTime)
			//if trimmed, endTime is one millisecond before the end of the day. We add it here to correctly calculate timeslots
			intervalBoundary = Math.min(timeInterval.endTime + eventType.eventBufferAfter + 1, timeInterval._endTimeBeforeTrim)
			isBoundaryExtended = true
		} else if(timeInterval._canExtendBufferAfter){
			intervalBoundary += eventType.eventBufferAfter
			isBoundaryExtended = true
		}

		if(isBoundaryExtended && ignoreDuration){
			eventEnd += eventType.eventBufferAfter
		}
		console.log("timeInterval.endTime = ", dayjs(timeInterval.endTime).format("MMM DD hh:mm:ss a") , " (" + timeInterval.endTime + ")")
		console.log("timeInterval.eventBufferAfter = ", eventType.eventBufferAfter / (60*1000))
		console.log("timeInterval._endTimeBeforeTrim = ", dayjs(timeInterval._endTimeBeforeTrim).format("MMM DD hh:mm:ss a") , " (" + timeInterval._endTimeBeforeTrim + ")")
		console.log("intervalBoundary = ", dayjs(intervalBoundary).format("MMM DD hh:mm:ss a") , " (" + intervalBoundary + ")")
		console.log("timeInterval = " + timeInterval)

		// console.log("getAvailabilityHoursFromTimeInterval: intervalBoundary = " + dayjs(intervalBoundary).format("hh:mm a"))
		// console.log("getAvailabilityHoursFromTimeInterval: currentTime = " + dayjs(currentTime).format("hh:mm a") + ", currentTime + eventEnd = "  + dayjs(currentTime + eventEnd).format("hh:mm a"))
		while (currentTime + eventEnd <= intervalBoundary){
			if(currentTime > minTime && currentTime <= maxTime){
				res.push(currentTime);
			}
			currentTime = currentTime + eventType.increments;
			// console.log("getAvailabilityHoursFromTimeInterval: currentTime = " + dayjs(currentTime).format("MMM DD") + " " + dayjs(currentTime).format("hh:mm a") + ", timeInterval.endTime = " + dayjs(timeInterval.endTime).format("hh:mm a") + ", timeInterval.endTime = " + timeInterval.endTime)
		}
		// console.log("getAvailabilityHoursFromTimeInterval: timeInterval = " + timeInterval + ", eventType = ", eventType)
		return res;
	}

	getTimezoneOffsetMs(){
		let timezoneOffset = new Date().getTimezoneOffset()
		console.log("intervals1 timezoneOffset = ", timezoneOffset)
		return -timezoneOffset;
	}

	transposeToCurrentTimezone(dateStart, dateEnd, eventType, timezoneDifference){
		let settings = new AvailabilitySettings()
		settings._timezoneDifference = timezoneDifference
		this._timezoneDifference = timezoneDifference

		console.log("transposeToCurrentTimezone")

		enumerateDaysBetweenDates(dateStart, dateEnd).forEach(date => {
			console.log("transposeToCurrentTimezone: date = ", date)
			let intervalFn = (date, eventType) => this.intervalsByDateInternal(date, eventType, true)
			let intervals = this.threeDaysIntervals(date, eventType, intervalFn)
			if(intervals){
				intervals.forEach(interval => interval._alreadyTransposed = true)
				settings.setOneTimeIntervalsByDate(date, intervals)
			}
		})
		console.log("transposeToCurrentTimezone complete")

		return settings
	}

	isTimeIntervalAvailable(timeInterval, eventType){
		let res = false
		enumerateDaysBetweenDates(timeInterval.startTime, timeInterval.endTime).forEach(date => {
			let intervals = this.intervalsByDate(date, eventType)
			console.log("isTimeIntervalAvailable: intervals = ", intervals, ", timeInterval = ", timeInterval)
			console.log("isTimeIntervalAvailable: intervals = " + intervals, ", timeInterval = " + timeInterval)

			if(intervals){
				intervals.forEach(interval => {
					if(this.isIntervalFullyContained(interval, timeInterval)){
						res = true
					}
				})
			}
		})
		return res
	}

	getRecurringDaysAsArray(){
		return [this.sun, this.mon, this.tue, this.wed, this.thu, this.fri, this.sat]
	}

	transposeBy(ms: number){
		let settings = new AvailabilitySettings()
		let days = this.getRecurringDaysAsArray()
		for(let i = 0; i < 7; ++i){
			let day = days[i];
			let leftDay = settings.byDayIndex((i + 6) % 7)
			let centerDay = settings.byDayIndex(i)
			let rightDay = settings.byDayIndex((i + 1) % 7)

			let intervals = {
				leftDays: [],
				centerDays: [],
				rightDays: [],
			}
			day.forEach(interval => {
				let originalStart = new Date(interval.startTime)
				let originalEnd = new Date(interval.endTime)


				let leftDayStart
				let leftDayEnd
				let centerDayStart = new Date(interval.startTime)
				let centerDayEnd = new Date(interval.endTime)
				let rightDayStart
				let rightDayEnd

				let centerDayStartTs = new Date(interval.startTime + ms)
				let centerDayEndTs = new Date(interval.endTime + ms)

				let originalStartDay = originalStart.getDay()
				let originalEndDay = originalEnd.getDay()
				let centerDayStartDay = centerDayStartTs.getDay()
				let centerDayEndDay = centerDayEndTs.getDay()

				console.log("intervals1 centerDayEndDay = ", centerDayEndDay, ", originalStartDay = ", originalStartDay)

				if(centerDayStartDay < originalStartDay){
					leftDayStart = new Date(centerDayStart)
					if(centerDayEndDay < originalStartDay){
						leftDayEnd = new Date(centerDayEnd)
						centerDayStart = null
						centerDayEnd = null
					} else {
						leftDayEnd = new Date(centerDayStart)
						leftDayEnd.setUTCHours(23, 59, 59)
						centerDayStart.setUTCHours(0, 0, 0)
					}
				}

				if(centerDayEndDay > originalEndDay){
					rightDayEnd = new Date(centerDayEnd)
					if(centerDayStartDay > originalEndDay){
						rightDayStart = new Date(centerDayStart)
						centerDayStart = null
						centerDayEnd = null
					} else {
						rightDayStart = new Date(centerDayEnd)
						rightDayStart.setUTCHours(0, 0, 0)
						centerDayEnd.setUTCHours(23, 59, 59)
					}
				}

				if(leftDayStart){
					intervals.leftDays.push(new TimeInterval(leftDayStart.getTime(), leftDayEnd.getTime()))
				}
				if(centerDayStart){
					intervals.centerDays.push(new TimeInterval(centerDayStart.getTime(), centerDayEnd.getTime()))
				}
				if(rightDayStart){
					intervals.rightDays.push(new TimeInterval(rightDayStart.getTime(), rightDayEnd.getTime()))
				}
			})

			console.log("intervals1 = ", intervals)
			leftDay.push(...intervals.leftDays)
			centerDay.push(...intervals.centerDays)
			rightDay.push(...intervals.rightDays)
		}
		return settings;
	}
}

